/**
 * SkipNavigationLink Component
 * 
 * Provides a skip-to-main-content link for keyboard and screen reader users.
 * This link is visually hidden until focused, meeting WCAG 2.1 Level A requirements.
 * 
 * Implementation follows:
 * - WCAG 2.1 Success Criterion 2.4.1 (Bypass Blocks)
 * - Mobile-First Accessibility Runbook Phase 1
 * - Accessibility Standardization Implementation Runbook Task 0.1
 */

import React from 'react';

interface SkipNavigationLinkProps {
  /** Target ID of the main content element. Default: 'main-content' */
  targetId?: string;
  /** Custom label for the skip link. Default: 'Skip to main content' */
  label?: string;
}

export const SkipNavigationLink: React.FC<SkipNavigationLinkProps> = ({
  targetId = 'main-content',
  label = 'Skip to main content',
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    
    if (target) {
      // Move focus to main content
      target.focus();
      
      // If element is not focusable by default, set tabindex temporarily
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      
      // Scroll into view with smooth behavior (respecting prefers-reduced-motion)
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-all"
    >
      {label}
    </a>
  );
};
